import { UploadStatus } from '@msteams/services-io-browser-file-upload';
import { DocusignConstants } from '../../common/DocusignConstants';
import { ApprovalRequestType } from '../../data/types.generated';
import { PanelRegion, PanelType, ScenarioName, StepName, SubWorkLoad } from '../../logger/interfaces';

import { LogCategory } from '../../logger/LogCategories';
import { StatusName } from '../../logger/ScenarioLogger';
import { DocusignFileAttachment } from '../../models/fileService/DocusignFileAttachment';
import { FileService } from '../../models/fileService/FileService';
import { newGuid } from '../../utilities/AppUtils';
import { getAppHost } from '../../utilities/TeamsUtils';
import { stringFormat } from '../../utilities/Utils';
import { uploadFileEsignGateway } from './UploadFileEsignGateway';

// This array holds all the accepted file types in Docusign.
// https://developers.docusign.com/docs/esign-rest-api/esign101/concepts/documents/
export const acceptedFileTypes = [
    'application/msword', // .doc .dot
    'application/vnd.ms-word.document.macroEnabled.12', // .docm
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template', // .dotm
    'application/vnd.ms-word.template.macroEnabled.12', // .dotx
    'text/html', // .htm .html
    'application/vnd.ms-outlook', // .msg
    'application/pdf', // .pdf
    'application/rtf', // .rtf
    'text/plain', // .txt
    'application/vnd.wordperfect', // .wpd
    'application/vnd.ms-xpsdocument', // .xps
    'image/bmp', // .bmp
    'image/gif', // .gif
    'image/jpg', // .jpg
    'image/jpeg', // .jpeg
    'image/png', // .png
    'image/tiff', // .tif .tiff
    'application/vnd.ms-powerpoint', // .pot .pps .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.template', // .potx
    'application/vnd.ms-powerpoint.presentation.macroEnabled.12', // .pptm
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'text/csv', // .csv
    'application/vnd.ms-excel', // .xls
    'application/vnd.ms-excel.sheet.macroEnabled.12', // .xlsm
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
];

export interface DocusignResponseBody {
    envelopeId: string;
    uri: string;
    statusDateTime: Date;
    status: string;
}

export class DocusignFileService implements FileService<DocusignFileAttachment> {
    private logger = getAppHost().getLogger();
    private loc = getAppHost().getLocale();

    async uploadFile(
        file: File,
        notifier: (progress: number, status: UploadStatus) => void
    ): Promise<Partial<DocusignFileAttachment>> {
        this.logger.logTraceInfo(LogCategory.DocusignFileUpload, `Calling ${DocusignFileService.name} - uploadFile`);
        this.logger.extendScenario(ScenarioName.DocusignFileUpload, ScenarioName.CreateApprovals);

        const {
            docusign_upload_error_default,
            docusign_upload_error_canceled,
            docusign_upload_error_open_window_failed,
        } = this.loc.strings;

        const docusignUploadId = newGuid();
        this.logger.logTraceInfo(LogCategory.DocusignFileUpload, 'DocusignFileUpload');
        this.logger.markScenario(ScenarioName.DocusignFileUpload, StepName.DocusignFileUploadStart, StatusName.NA, {
            data: {
                docusignUploadId,
            },
        });
        this.logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateApprovals,
            StepName.FileUploadSelected,
            'click',
            'nav',
            'docusignFileService',
            'button',
            PanelRegion.Modal,
            PanelType.CreateApproval,
            {
                docusignUploadId,
                fileType: file.type,
                fileSize: file.size,
                ApprovalType: ApprovalRequestType.Docusign,
            }
        );

        const docusignFileAttachment: Partial<DocusignFileAttachment> = {
            documentName: file.name,
        };

        try {
            this.preCheck(file);

            docusignFileAttachment.documentId = await uploadFileEsignGateway(ApprovalRequestType.Docusign, file);

            notifier(100, UploadStatus.COMPLETE);
            this.logger.logTraceInfo(LogCategory.DocusignFileUpload, 'Docusign file upload succeeded');
            this.logger.markScenario(ScenarioName.DocusignFileUpload, StepName.DocusignFileUploadEnd, StatusName.NA, {
                data: {
                    docusignUploadId,
                    documentId: docusignFileAttachment.documentId,
                },
            });
            this.logger.stopScenario(ScenarioName.DocusignFileUpload, {
                data: {
                    docusignUploadId,
                    documentId: docusignFileAttachment.documentId,
                },
            });

            return docusignFileAttachment;
        } catch (error: any) {
            let message: string = null;

            this.logger.markScenario(ScenarioName.DocusignFileUpload, StepName.DocusignFileUploadFail, StatusName.NA, {
                data: {
                    docusignUploadId,
                },
            });
            this.logger.failScenario(ScenarioName.DocusignFileUpload, {
                data: {
                    docusignUploadId,
                },
            });

            if (error && (typeof error === 'string' || error instanceof String)) {
                const errorMessage = error.toString();

                // If it's a message
                if (errorMessage === 'CancelledByUser') {
                    // Upload canceled
                    message = docusign_upload_error_canceled;
                } else if (errorMessage === 'FailedToOpenWindow') {
                    // Authentication window blocked or can't be opened
                    message = docusign_upload_error_open_window_failed;
                } else {
                    message = errorMessage;
                }
            } else {
                // Default error message
                message = stringFormat(docusign_upload_error_default, { file: file.name });
            }

            this.logger.logTraceException(
                LogCategory.DocusignFileUpload,
                message,
                {
                    error: error instanceof Error ? error : undefined,
                    method: 'uploadFile',
                },
                {
                    docusignUploadId,
                }
            );

            throw new Error(message);
        }
    }

    private preCheck(file: File): void {
        if (!acceptedFileTypes.includes(file.type)) {
            throw new Error(this.loc.strings.docusign_upload_error_invalid_types);
        }
        if (file.size > DocusignConstants.maxFileUploadSize) {
            throw new Error(this.loc.strings.esign_upload_error_file_too_large);
        }
    }
}

let docusignFileService: DocusignFileService = null;

export function getDocusignFileService(): DocusignFileService {
    if (!docusignFileService) {
        docusignFileService = new DocusignFileService();
    }
    return docusignFileService;
}
